/**
 * Custom Font Storage Service
 * Handles persistence and management of user-uploaded custom fonts
 */

import { get, set, del } from "idb-keyval";
import { authorization } from "module/auth";

export interface CustomFontData {
  name: string;
  dataUrl: string; // Base64 data URL of the font file
  mimeType: string;
  weight?: string;
  style?: string;
  uploadedAt: number;
}

export interface CustomFontMetadata {
  name: string;
  weight?: string;
  style?: string;
  uploadedAt: number;
}

interface CustomFontStorageData {
  userId?: string;
  clientId?: number;
  fonts: CustomFontData[];
  metadata: CustomFontMetadata[];
}

class CustomFontStorageService {
  private static readonly STORAGE_KEY = "custom_fonts";
  private static readonly FONT_METADATA_KEY = "custom_fonts_metadata";
  private static readonly STORAGE_METADATA_KEY = "custom_fonts_storage_metadata"; // userId/clientId
  // In-memory cache to avoid repeated loads in the same session
  private cache: {
    userId: string;
    clientId: number;
    fonts: CustomFontData[];
    metadata: CustomFontMetadata[];
  } | null = null;

  /**
   * Store a custom font with its data and metadata
   */
  async storeCustomFont(fontData: CustomFontData): Promise<void> {
    try {
      const user = authorization.getUser();
      const currentUserId = user?.id;
      const currentClientId = user?.clientId;

      if (!currentUserId || !currentClientId) {
        console.warn("No user logged in or missing userId/clientId, cannot store font");
        return;
      }

      // Get existing fonts with validation
      const existingFonts = await this.getCustomFonts();
      const existingMetadata = await this.getCustomFontsMetadata();

      // Remove any existing font with the same name
      const filteredFonts = existingFonts.filter((font) => font.name !== fontData.name);
      const filteredMetadata = existingMetadata.filter((meta) => meta.name !== fontData.name);

      // Add new font data
      const updatedFonts = [...filteredFonts, fontData];
      const updatedMetadata = [
        ...filteredMetadata,
        {
          name: fontData.name,
          weight: fontData.weight,
          style: fontData.style,
          uploadedAt: fontData.uploadedAt,
        },
      ];

      // Store both data and metadata
      await set(CustomFontStorageService.STORAGE_KEY, updatedFonts);
      await set(CustomFontStorageService.FONT_METADATA_KEY, updatedMetadata);

      // Store userId and clientId for validation
      await set(CustomFontStorageService.STORAGE_METADATA_KEY, {
        userId: currentUserId,
        clientId: currentClientId,
      });

      // Update in-memory cache
      this.cache = {
        userId: currentUserId,
        clientId: currentClientId,
        fonts: updatedFonts,
        metadata: updatedMetadata,
      };

      console.log(`✅ Custom font "${fontData.name}" stored successfully`);
    } catch (error) {
      console.error("Error storing custom font:", error);
      throw new Error(`Failed to store custom font: ${error}`);
    }
  }

  /**
   * Retrieve all stored custom fonts (with userId/clientId validation)
   */
  async getCustomFonts(): Promise<CustomFontData[]> {
    try {
      const user = authorization.getUser();
      const currentUserId = user?.id;
      const currentClientId = user?.clientId;

      // If no user is logged in, don't return any fonts
      if (!currentUserId || !currentClientId) {
        console.warn("No user logged in, clearing custom fonts");
        await this.clearAllCustomFonts();
        return [];
      }

      // Check storage metadata
      const storageMetadata = await get<{ userId?: string; clientId?: number }>(
        CustomFontStorageService.STORAGE_METADATA_KEY
      );

      // If metadata exists, validate userId and clientId
      if (storageMetadata) {
        if (storageMetadata.userId !== currentUserId || storageMetadata.clientId !== currentClientId) {
          console.warn("Custom fonts belong to different user/client, clearing them", {
            storedUserId: storageMetadata.userId,
            currentUserId,
            storedClientId: storageMetadata.clientId,
            currentClientId,
          });
          await this.clearAllCustomFonts();
          return [];
        }
      } else {
        // Old format - no metadata, clear it for security
        await this.clearAllCustomFonts();
        return [];
      }

      // In-memory cache
      if (
        this.cache &&
        this.cache.userId === currentUserId &&
        this.cache.clientId === currentClientId &&
        Array.isArray(this.cache.fonts)
      ) {
        return this.cache.fonts;
      }

      const fonts = (await get<CustomFontData[]>(CustomFontStorageService.STORAGE_KEY)) || [];
      const metadata = (await get<CustomFontMetadata[]>(CustomFontStorageService.FONT_METADATA_KEY)) || [];
      this.cache = { userId: currentUserId, clientId: currentClientId, fonts, metadata };
      return fonts;
    } catch (error) {
      console.error("Error retrieving custom fonts:", error);
      return [];
    }
  }

  /**
   * Retrieve only metadata of custom fonts (lighter data for font lists)
   */
  async getCustomFontsMetadata(): Promise<CustomFontMetadata[]> {
    try {
      const user = authorization.getUser();
      const currentUserId = user?.id;
      const currentClientId = user?.clientId;

      // If no user is logged in, don't return any fonts
      if (!currentUserId || !currentClientId) {
        return [];
      }

      // Check storage metadata
      const storageMetadata = await get<{ userId?: string; clientId?: number }>(
        CustomFontStorageService.STORAGE_METADATA_KEY
      );

      // If metadata exists, validate userId and clientId
      if (storageMetadata) {
        if (storageMetadata.userId !== currentUserId || storageMetadata.clientId !== currentClientId) {
          return [];
        }
      } else {
        // Old format - no metadata, return empty
        return [];
      }

      // In-memory cache
      if (
        this.cache &&
        this.cache.userId === currentUserId &&
        this.cache.clientId === currentClientId &&
        Array.isArray(this.cache.metadata)
      ) {
        return this.cache.metadata;
      }

      const metadata = (await get<CustomFontMetadata[]>(CustomFontStorageService.FONT_METADATA_KEY)) || [];
      const fonts = (await get<CustomFontData[]>(CustomFontStorageService.STORAGE_KEY)) || [];
      this.cache = { userId: currentUserId, clientId: currentClientId, fonts, metadata };
      return metadata;
    } catch (error) {
      console.error("Error retrieving custom fonts metadata:", error);
      return [];
    }
  }

  /**
   * Get a specific custom font by name
   */
  async getCustomFont(fontName: string): Promise<CustomFontData | null> {
    try {
      const fonts = await this.getCustomFonts();
      return fonts.find((font) => font.name === fontName) || null;
    } catch (error) {
      console.error(`Error retrieving custom font "${fontName}":`, error);
      return null;
    }
  }

  /**
   * Delete a custom font
   */
  async deleteCustomFont(fontName: string): Promise<void> {
    try {
      const existingFonts = await this.getCustomFonts();
      const existingMetadata = await this.getCustomFontsMetadata();

      const updatedFonts = existingFonts.filter((font) => font.name !== fontName);
      const updatedMetadata = existingMetadata.filter((meta) => meta.name !== fontName);

      await set(CustomFontStorageService.STORAGE_KEY, updatedFonts);
      await set(CustomFontStorageService.FONT_METADATA_KEY, updatedMetadata);

      console.log(`✅ Custom font "${fontName}" deleted successfully`);
    } catch (error) {
      console.error(`Error deleting custom font "${fontName}":`, error);
      throw new Error(`Failed to delete custom font: ${error}`);
    }
  }

  /**
   * Load all stored custom fonts into document.fonts (with userId/clientId validation)
   * Loads all fonts in parallel for better performance
   */
  async loadStoredFontsIntoDocument(): Promise<string[]> {
    const loadedFontNames: string[] = [];

    try {
      const customFonts = await this.getCustomFonts(); // This now validates userId/clientId

      if (customFonts.length === 0) {
        return loadedFontNames;
      }

      // Load all fonts in parallel instead of sequentially
      const fontLoadPromises = customFonts.map(async (fontData) => {
        try {
          // Check if font is already loaded
          const existingFont = Array.from(document.fonts).find(
            (font) => font.family.toLowerCase() === fontData.name.toLowerCase()
          );

          if (existingFont && existingFont.status === "loaded") {
            return { success: true, name: fontData.name, alreadyLoaded: true };
          }

          // Create and load the font
          const fontFace = new FontFace(fontData.name, `url(${fontData.dataUrl})`, {
            weight: fontData.weight || "normal",
            style: fontData.style || "normal",
          });

          await fontFace.load();
          document.fonts.add(fontFace);

          return { success: true, name: fontData.name, alreadyLoaded: false };
        } catch (error) {
          console.error(`Failed to load custom font "${fontData.name}":`, error);
          return { success: false, name: fontData.name, error };
        }
      });

      // Wait for all fonts to load (or fail)
      const results = await Promise.allSettled(fontLoadPromises);

      // Collect successfully loaded fonts
      results.forEach((result) => {
        if (result.status === "fulfilled" && result.value.success) {
          loadedFontNames.push(result.value.name);
        }
      });

      const newlyLoaded = results.filter(
        (r) => r.status === "fulfilled" && r.value.success && !r.value.alreadyLoaded
      ).length;
      const alreadyLoaded = results.filter(
        (r) => r.status === "fulfilled" && r.value.success && r.value.alreadyLoaded
      ).length;
      const failed = results.filter((r) => r.status === "rejected" || !r.value?.success).length;

      console.log(
        `✅ Loaded ${loadedFontNames.length}/${customFonts.length} custom fonts in parallel ` +
          `(${newlyLoaded} new, ${alreadyLoaded} cached, ${failed} failed)`
      );
    } catch (error) {
      console.error("Error loading stored fonts:", error);
    }

    return loadedFontNames;
  }

  /**
   * Get custom fonts data for export (includes base64 data for embedding)
   */
  async getCustomFontsForExport(): Promise<Map<string, CustomFontData>> {
    const fontsMap = new Map<string, CustomFontData>();

    try {
      const customFonts = await this.getCustomFonts(); // This now validates userId/clientId
      customFonts.forEach((font) => {
        fontsMap.set(font.name.toLowerCase(), font);
      });
    } catch (error) {
      console.error("Error getting custom fonts for export:", error);
    }

    return fontsMap;
  }

  /**
   * Clear all custom fonts (useful for debugging/cleanup)
   */
  async clearAllCustomFonts(): Promise<void> {
    try {
      await del(CustomFontStorageService.STORAGE_KEY);
      await del(CustomFontStorageService.FONT_METADATA_KEY);
      await del(CustomFontStorageService.STORAGE_METADATA_KEY);
      this.cache = null;
    } catch (error) {
      console.error("Error clearing custom fonts:", error);
      throw new Error(`Failed to clear custom fonts: ${error}`);
    }
  }

  /**
   * Get storage statistics
   */
  async getStorageStats(): Promise<{
    totalFonts: number;
    totalSize: number;
    fonts: Array<{ name: string; size: number; uploadedAt: number }>;
  }> {
    try {
      const customFonts = await this.getCustomFonts(); // This now validates userId/clientId
      let totalSize = 0;

      const fonts = customFonts.map((font) => {
        // Approximate size calculation (base64 is ~33% larger than binary)
        const size = Math.round(font.dataUrl.length * 0.75);
        totalSize += size;

        return {
          name: font.name,
          size,
          uploadedAt: font.uploadedAt,
        };
      });

      return {
        totalFonts: customFonts.length,
        totalSize,
        fonts,
      };
    } catch (error) {
      console.error("Error getting storage stats:", error);
      return {
        totalFonts: 0,
        totalSize: 0,
        fonts: [],
      };
    }
  }
}

// Export singleton instance
export const customFontStorage = new CustomFontStorageService();
